import { useState } from 'react'
import Styles from './Stock.module.scss'
import { useUnsplashImages } from 'hooks/queries/useUnsplashImages'
import { useDebounce } from 'hooks/useDebounce'
import { SearchField } from '../SearchField/SearchField'
import { useImagePickerContext } from '../ImagePickerContext'
import { LoadingState } from '../LoadingState/LoadingState'
import { Grid } from '../Grid/Grid'
import InfiniteScroll from 'common/InfiniteScroll/InfiniteScroll'
import { ScrollContainer } from '../ScrollContainer/ScrollContainer'
import { Portal } from 'components/Portal/Portal'
import { SelectionCount } from '../SelectionCount'
import { DeselectAllButton } from '../DeselectAllButton'
import { ConfirmSelectionButton } from '../ConfirmSelectionButton'
import { StockProps } from './Types'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { useConfirm } from './useConfirm'
import { ImageThumbnail } from '../ImageThumbnail/ImageThumbnail'
import cx from 'classnames'
import { usePixabayElements } from 'hooks/queries/designer/usePixabayElements'
import { SelectedImage } from 'types'

export const Stock = ({ actionsContainerRef, containerClassName, confirmLabel }: StockProps) => {
  const { initialQuery, selectedImages, toggleSelectedImage } = useImagePickerContext()
  const [query, setQuery] = useState(initialQuery || '')
  const debouncedQuery = useDebounce(query, 500)
  const {
    data: unsplashData,
    fetchNextPage: unsplashFetchNextPage,
    hasNextPage: unsplashHasNextPage,
  } = useUnsplashImages({ query: debouncedQuery, perPage: 24 })
  const {
    data: pixabayData,
    fetchNextPage: pixabayFetchNextPage,
    hasNextPage: pixabayHasNextPage,
  } = usePixabayElements({ query: debouncedQuery, pageSize: 24, imageType: 'all' })
  const unsplashResults = (unsplashData?.pages || []).flatMap((page) =>
    page?.type === 'success' ? (page.data.results || page.data) ?? [] : [],
  )
  const { isMobile } = useResponsiveDesign()

  const unsplashImages = unsplashResults.map((item) => {
    return {
      url: item.urls.regular,
      height: item.height,
      width: item.width,
      unsplash: {
        authorUsername: item.user.username,
        authorName: item.user.name,
        id: item.id,
        query,
      },
    }
  })
  const pixabayResults = (pixabayData?.pages || []).flatMap((page) => page.images || [])
  const pixabayImages = pixabayResults.map((item) => {
    return {
      url: item.largeImageURL,
      height: item.imageHeight,
      width: item.imageWidth,
      pixabay: {
        id: item.id,
      },
    }
  })

  const images: SelectedImage[] = []
  const maxLength = Math.max(unsplashImages.length, pixabayImages.length)

  for (let i = 0; i < maxLength; i++) {
    if (i < unsplashImages.length) {
      images.push(unsplashImages[i])
    }
    if (i < pixabayImages.length) {
      images.push(pixabayImages[i])
    }
  }

  const hasNextPage = unsplashHasNextPage || pixabayHasNextPage
  const fetchNextPage = () => {
    unsplashFetchNextPage()
    pixabayFetchNextPage()
  }

  const { confirm, isConfirming } = useConfirm()

  return (
    <div className={cx(Styles.stock, containerClassName)} data-testid="stock-provider">
      {actionsContainerRef && (
        <Portal container={actionsContainerRef}>
          {isMobile && (
            <>
              <SelectionCount />
              <ConfirmSelectionButton
                onPress={confirm}
                isDisabled={selectedImages.length === 0 || isConfirming}
                confirmLabel={confirmLabel}
              />
            </>
          )}
          {!isMobile && selectedImages.length > 0 && (
            <>
              <SelectionCount />
              <DeselectAllButton />
              <ConfirmSelectionButton onPress={confirm} isDisabled={isConfirming} confirmLabel={confirmLabel} />
            </>
          )}
        </Portal>
      )}
      <SearchField value={query} onChange={setQuery} />

      {images.length === 0 && <LoadingState />}

      {images.length > 0 && (
        <ScrollContainer>
          <Grid.Root breakpointCols={isMobile ? 2 : 4}>
            {images.map((image) => (
              <Grid.Thumbnail key={image.unsplash?.id || image.pixabay?.id}>
                <ImageThumbnail
                  image={image}
                  onToggleSelect={toggleSelectedImage}
                  isSelected={selectedImages.some((i) => i.url === image.url)}
                  label={image.unsplash?.authorName}
                />
              </Grid.Thumbnail>
            ))}
          </Grid.Root>
          {hasNextPage && (
            <div className={Styles.infiniteLoader}>
              <InfiniteScroll hasMore={hasNextPage} loadMore={fetchNextPage} />
            </div>
          )}
        </ScrollContainer>
      )}
    </div>
  )
}
